import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import needle from 'needle';
import { firstValueFrom, map } from 'rxjs';
import type { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { newProductsInfoMail } from '../server/logistikMailTemplates.js';
import { chancenWerkMailer } from '../server/mailer.js';
import { LogistikProduct } from './LogistikProduct.js';
import { Role } from './semantics/Role.js';

@Entity()
export class LogistikInfoMail extends AdornisEntity {
  static override _class = 'HubInfoMail'; // TODO needs migration before we can change this

  @validate(nonOptional())
  @Field(type => String, { default: v => v ?? 'Neue Produkte in der CHANCENlogistik' })
  subject!: string;

  @Field(type => ID)
  bannerFileID: Maybe<string>;

  @Field(type => String)
  introText: Maybe<string>;

  @Field(type => String)
  outroText: Maybe<string>;

  @validate(nonOptional())
  @Field(type => [String], { default: v => v ?? [] })
  states!: string[];

  @validate(nonOptional())
  @Field(type => [ID], { default: v => v ?? [] })
  productIDs!: string[];

  @Field(type => [LogistikProduct], {
    resolve: function (this: LogistikInfoMail) {
      return async (gqlFields: BaseQLSelectionSet<LogistikProduct>) => this.products;
    },
  })
  products: Maybe<LogistikProduct[]>;

  @validate(({ value, key, target }) => {
    if (
      !value ||
      !Array.isArray(value) ||
      !value.length ||
      value.some(valueEntry => !Object.values(Role).includes(valueEntry))
    )
      throw new ValidationError(
        'Es dürfen nur folgende Nutzerrollen ausgewählt werden: Lernkoordinatoren, Schulkoordinatoren, Programmleiter. Mindestens eine davon muss ausgewählt sein',
        {
          key,
          target,
          translationKey: 'only_valid_roles',
        },
      );
  })
  @Field(type => [String], { default: v => v ?? [] })
  userRoles!: Role[];

  @Mutation(type => Boolean)
  static sendInfoMail(@Arg('infoMail', type => LogistikInfoMail) infoMail: LogistikInfoMail) {
    return async () => {
      if (context.serverContext || !context.userID)
        throw new Error('Produkt-Update-Mail kann nur vom Client und eingeloggt versandt werden');

      const user = (await context.user()) as Maybe<ChancenwerkUser>;
      if (!user) throw new Error('Eingeloggten Nutzer nicht gefunden');
      if (!user.hasPermission('LOGIN_LOGISTIK', 'GLOBAL'))
        throw new Error('Keine Berechtigung um eine Produkt-Update-Mail zu versenden');

      const mailAddresses = await this.getApplicableEmailAddresses(infoMail.userRoles, infoMail.states)();
      if (!mailAddresses.length) throw new Error('Keine Mail-Adresse gefunden für diese Nutzergruppenauswahl');

      return this._sendInfoMail(infoMail, mailAddresses);
    };
  }

  @Mutation(type => Boolean)
  static sendInfoTestMail(@Arg('infoMail', type => LogistikInfoMail) infoMail: LogistikInfoMail) {
    return async () => {
      if (context.serverContext || !context.userID)
        throw new Error('Test Mail kann nur vom Client und eingeloggt versandt werden');

      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ email: 1 });
      if (!user) throw new Error('Eingeloggten Nutzer nicht gefunden');

      return this._sendInfoMail(infoMail, [user.email]);
    };
  }

  @Query(type => [String])
  static getApplicableEmailAddresses(
    @Arg('userRoles', type => [String]) userRoles: Maybe<Role[]>,
    @Arg('states', type => [String]) states: Maybe<string[]>,
  ) {
    return async () => {
      if (!userRoles?.length) return [];

      const allMailAddresses = (
        await Promise.all(
          userRoles.map(
            async role =>
              await firstValueFrom(
                UserAPI.subscribeByFilter(
                  0,
                  0,
                  new ChancenwerkUserFilter({ roleID: role }),
                )({ _id: 1, email: 1, kontaktaufnahmeEmail: 1, adresse: { zipCity: { zip: 1 } } }).pipe(
                  map(employees =>
                    employees.filter(
                      employee => employee.email.includes('chancenwerk') || employee.kontaktaufnahmeEmail,
                    ),
                  ),
                ),
              ),
          ),
        )
      ).flat();

      if (!states?.length) return allMailAddresses.map(x => x.email);

      const validPostalCodes = (
        await Promise.all(
          states.map(async state => {
            const result = await needle('get', `https://zip-api.eu/api/v1/codes/state=DE-${state}`, { json: true });
            return result.body.map(state => state.postal_code);
          }),
        )
      ).flat();

      return allMailAddresses.filter(user => validPostalCodes.includes(user.adresse?.zipCity?.zip)).map(x => x.email);
    };
  }

  private static async _sendInfoMail(infoMail: LogistikInfoMail, mailAddresses: string[]) {
    const products = await LogistikProduct.getByID<LogistikProduct>(infoMail.productIDs)(LogistikProduct.allFields);
    infoMail.products = products;

    await chancenWerkMailer.sendMail(
      await Message.compose({ subject: infoMail.subject, html: newProductsInfoMail(infoMail) }),
      mailAddresses.map(address => new Recipient(address)),
    );

    return true;
  }
}
